import {
    type ParsedDocument,
} from "@magic-md/core";
import { t as translate } from "@magic-md/i18n";
import { useT } from "@magic-md/i18n/react";
import {
    ChevronDown,
    ChevronRight,
    Clipboard,
    Code2,
    Check,
    Clock,
    Activity,
    Eye,
    File,
    FileInput,
    FilePlus,
    FileText,
    FolderOpen,
    FolderPlus,
    Link,
    ListOrdered,
    MessageSquare,
    Package,
    RefreshCw,
    Save,
    Settings2,
    Share2,
    Star,
    Stethoscope,
    Trash2,
    Upload,
} from "lucide-react";
import {
    type ButtonHTMLAttributes,
    type KeyboardEvent as ReactKeyboardEvent,
    type MouseEvent as ReactMouseEvent,
    type ReactNode,
    type Ref,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    getAssetRefFromPath,
    getAssetRelativePathFromPath,
    getBundleDirFromPublishDirPath,
    isDocumentAssetsPath,
    isDocumentPublishDirPath,
    isFolderPublishDirPath,
} from "../lib/publish-utils";
import {
    buildOutputWatchVirtualChildren,
    getDocumentPathFromOutputWatchPath,
    getOutputWatchItemIdFromPath,
    isDocumentWatchItemPath,
    isDocumentWatchesPath,
} from "../lib/output-watch-persistence";
import {
    buildTraceVirtualChildren,
    getDocumentPathFromTracePath,
    getDocumentTraceDirPath,
    getTraceIdFromPath,
    isDocumentTraceDirPath,
    isDocumentTraceEmptyPath,
    isDocumentTraceItemPath,
    traceRunBelongsToDocument,
} from "../lib/trace-paths";
import {
    hasKnownDocumentPublishPreview,
    previewDocumentPublish,
} from "../lib/publish-preview";
import { cn } from "../lib/utils";
import { getPlatformService } from "../platform";
import type { FileTreeNode } from "../store/editor-store";
import { useEditorStore } from "../store/editor-store";
import { type SnapshotMeta, useHistoryStore } from "../store/history-store";
import { useLogStore } from "../store/log-store";
import { useOutputWatchStore } from "../store/output-watch-store";
import { usePublishStore } from "../store/publish-store";
import { type TraceRun, useTraceStore } from "../store/trace-store";
import {
    buildBundleVirtualChildren,
    buildFolderPublishVirtualChildren,
    buildPublishVirtualChildren,
    buildVirtualChildren,
} from "../store/editor/utils";
import { buildExtensionContextMenuItems } from "../extensions/extension-menu-bridge";
import { buildFileNodeMenuContext } from "../extensions/virtual-package-menu-context";
import { pluginUIRegistry } from "../plugins/plugin-ui-registry";
import { FileIcon } from "../theme/runtime/theme-icons";
import {
    dispatchAnnotationRebind,
    dispatchAnnotationSelect,
} from "./annotations/events";
import {
    loadAnnotationDocument,
    saveAnnotationDocument,
} from "./annotations/persistence";
import type {
    AnnotationStatus,
    PreviewAnnotation,
} from "./annotations/types";
import { ContextMenu, type ContextMenuItem } from "./ContextMenu";
import { showFilePickerDialog } from "./dialogs";
import { showDocumentPublishDialog } from "./dialogs/DocumentPublishDialog";
import { PluginPanel } from "./PluginPanel";

type WorkspaceEngine = ReturnType<typeof getPlatformService>["engine"];
const BUNDLE_REFRESH_EVENT = "magic-md:bundle-refresh";

type BundleRefreshDetail = {
    filePath?: string;
    bundleDir?: string;
    publishDir?: string;
    annotationDir?: string;
    traceDir?: string;
};

function getFileIcon(name: string, size: number) {
    return (
        <FileIcon
            name={name}
            size={size}
            className="shrink-0 text-muted-foreground"
            colorMode="currentColor"
        />
    );
}

function documentBundlePath(filePath: string): string | null {
    if (!/\.md$/i.test(filePath)) return null;
    return filePath.replace(/\.md$/i, ".magic-md");
}

function relativePath(path: string): string {
    const folders = useEditorStore.getState().workspaceFolders;
    const root = folders.find(
        (folder) => path === folder.path || path.startsWith(folder.path + "/"),
    );
    return root ? path.replace(root.path + "/", "") : path;
}

function getBundleDirFromConfigsPath(path: string): string | null {
    const marker = ".magic-md/configs";
    const index = path.indexOf(marker);
    if (index < 0) return null;
    return path.slice(0, index + ".magic-md".length);
}

function getConfigPathParts(path: string): string[] {
    const marker = ".magic-md/configs";
    const index = path.indexOf(marker);
    if (index < 0) return [];
    const rest = path.slice(index + marker.length).replace(/^\/+/, "");
    return rest ? rest.split("/").filter(Boolean) : [];
}

function getConfigsDeleteLabel(
    path: string,
    isDirectory: boolean,
    t: ReturnType<typeof useT>,
): string {
    if (!isDirectory) return t("explorer.configs.deleteFile");
    const [instanceId, source] = getConfigPathParts(path);
    if (!instanceId) return t("explorer.configs.deleteAll");
    if (source === "editor") return t("explorer.configs.deleteEditor");
    if (source === "render") return t("explorer.configs.deleteRender");
    return t("explorer.configs.deleteInstance", { instanceId });
}

function getConfigsDeleteConfirm(
    path: string,
    name: string,
    isDirectory: boolean,
    t: ReturnType<typeof useT>,
): string {
    if (!isDirectory) {
        return t("explorer.configs.confirmDeleteFile", { name });
    }
    const [instanceId, source] = getConfigPathParts(path);
    if (!instanceId) {
        return t("explorer.configs.confirmDeleteAll");
    }
    if (source === "editor") {
        return t("explorer.configs.confirmDeleteEditor", { instanceId });
    }
    if (source === "render") {
        return t("explorer.configs.confirmDeleteRender", { instanceId });
    }
    return t("explorer.configs.confirmDeleteInstance", { instanceId });
}

function isDocumentAnnotationsPath(path: string): boolean {
    return /\.magic-md\/annotations(?:\/|$)/.test(path);
}

function isDocumentAnnotationsRootPath(path: string): boolean {
    return /\.magic-md\/annotations$/.test(path);
}

function isDocumentAnnotationStatusPath(path: string): boolean {
    return /\.magic-md\/annotations\/(?:open|resolved|orphaned)$/.test(path);
}

function isDocumentAnnotationItemPath(path: string): boolean {
    return /\.magic-md\/annotations\/(?:open|resolved|orphaned)\/[^/]+$/.test(path);
}

function getAnnotationPathInfo(path: string): {
    documentPath: string;
    annotationsRoot: string;
    status?: AnnotationStatus;
    annotationId?: string;
} | null {
    const match = /^(.*\.magic-md)\/annotations(?:\/(open|resolved|orphaned)(?:\/([^/]+))?)?$/.exec(path);
    if (!match) return null;
    return {
        documentPath: `${match[1].replace(/\.magic-md$/i, "")}.md`,
        annotationsRoot: `${match[1]}/annotations`,
        status: match[2] as AnnotationStatus | undefined,
        annotationId: match[3] ? decodeURIComponent(match[3]) : undefined,
    };
}

function getBundleDirFromAnnotationsPath(path: string): string | null {
    const marker = ".magic-md/annotations";
    const index = path.indexOf(marker);
    if (index < 0) return null;
    return path.slice(0, index + ".magic-md".length);
}

function getConfigInstanceIcon(instanceId: string, parsedDocument: ParsedDocument | null | undefined): ReactNode {
    const definition = parsedDocument?.definitions.get(instanceId);
    if (definition) {
        const icon = pluginUIRegistry.get(definition.pluginName)?.manifest.icon;
        return <span className="text-[12px] leading-none">{icon ?? "🧩"}</span>;
    }
    if (parsedDocument?.contentDefinitions?.has(instanceId)) {
        return <FileText size={14} className="text-muted-foreground" />;
    }
    if (parsedDocument?.forDefinitions?.has(instanceId)) {
        return <ListOrdered size={14} className="text-muted-foreground" />;
    }
    if (parsedDocument?.uses?.some((binding) => binding.id === instanceId)) {
        return <Share2 size={14} className="text-muted-foreground" />;
    }
    if (parsedDocument?.imports?.some((binding) => binding.namespace === instanceId)) {
        return <FileInput size={14} className="text-muted-foreground" />;
    }
    return <Package size={14} className="text-muted-foreground" />;
}

function getVirtualBundleIcon(node: FileTreeNode, parsedDocument: ParsedDocument | null | undefined): ReactNode {
    if (
        isDocumentTraceDirPath(node.path) ||
        isDocumentTraceItemPath(node.path) ||
        isDocumentTraceEmptyPath(node.path)
    ) {
        return <Activity size={14} className="text-muted-foreground" />;
    }

    if (isDocumentWatchesPath(node.path) || isDocumentWatchItemPath(node.path)) {
        return <Eye size={14} className="text-muted-foreground" />;
    }

    if (isDocumentAnnotationsPath(node.path)) {
        if (!node.isDirectory) return getFileIcon(node.name, 14);
        return <MessageSquare size={14} className="text-muted-foreground" />;
    }

    if (!node.isDirectory) return getFileIcon(node.name, 14);

    const configParts = getConfigPathParts(node.path);
    if (/\.magic-md\/configs(?:\/|$)/.test(node.path)) {
        if (configParts.length === 0) {
            return <Settings2 size={14} className="text-muted-foreground" />;
        }
        if (configParts.length === 1) {
            return getConfigInstanceIcon(configParts[0], parsedDocument);
        }
        if (configParts[1] === "editor") {
            return <Code2 size={14} className="text-muted-foreground" />;
        }
        if (configParts[1] === "render") {
            return <Eye size={14} className="text-muted-foreground" />;
        }
    }

    if (/\.magic-md\/history$/.test(node.path)) {
        return <Clock size={14} className="text-muted-foreground" />;
    }
    if (/\.magic-md\/publish$/.test(node.path)) {
        return <Upload size={14} className="text-muted-foreground" />;
    }
    if (/\.magic-md\/outputs$/.test(node.path)) {
        return <FileText size={14} className="text-muted-foreground" />;
    }
    if (isDocumentAssetsPath(node.path)) {
        return <FolderOpen size={14} className="text-muted-foreground" />;
    }
    return <Package size={14} className="text-muted-foreground" />;
}

async function safeReadDir(
    engine: WorkspaceEngine,
    dirPath: string,
): Promise<Array<{ name: string; isDirectory: boolean }>> {
    try {
        const { entries } = await engine.workspaceReadDir({
            path: dirPath,
            allowMissing: true,
        });
        return entries;
    } catch {
        return [];
    }
}

function traceIdsFromEntries(
    traceDirPath: string,
    entries: Array<{ name: string; isDirectory: boolean }>,
): string[] {
    return entries
        .filter((entry) => !entry.isDirectory)
        .map((entry) => getTraceIdFromPath(`${traceDirPath}/${entry.name}`))
        .filter((traceId): traceId is string => !!traceId);
}

async function loadVirtualChildren(node: FileTreeNode): Promise<FileTreeNode[]> {
    const engine = getPlatformService().engine;

    if (!node.isDirectory && node.bundlePath) {
        const entries = await safeReadDir(engine, node.bundlePath);
        return buildBundleVirtualChildren(node.bundlePath, entries);
    }

    if (node.isVirtual && isDocumentAnnotationsPath(node.path)) {
        return loadAnnotationVirtualChildren(engine, node.path);
    }

    if (node.isVirtual && isDocumentWatchesPath(node.path)) {
        return buildOutputWatchVirtualChildren(engine, node.path);
    }

    if (node.isVirtual && isDocumentTraceDirPath(node.path)) {
        const documentPath = getDocumentPathFromTracePath(node.path);
        const runs = useTraceStore.getState().runs.filter((run) =>
            traceRunBelongsToDocument(run, documentPath),
        );
        const entries = await safeReadDir(engine, node.path);
        return buildTraceVirtualChildren(node.path, runs, traceIdsFromEntries(node.path, entries));
    }

    if (node.isVirtual && isDocumentPublishDirPath(node.path)) {
        const bundlePath = getBundleDirFromPublishDirPath(node.path);
        if (!bundlePath) return [];
        const publishEntries = await safeReadDir(engine, node.path);
        return buildPublishVirtualChildren(bundlePath, publishEntries);
    }

    if (node.isVirtual && isFolderPublishDirPath(node.path)) {
        const entries = await safeReadDir(engine, node.path);
        return buildFolderPublishVirtualChildren(node.path, entries);
    }

    if (node.isVirtual || node.isDirectory) {
        const entries = await safeReadDir(engine, node.path);
        return buildVirtualChildren(node.path, entries);
    }

    return [];
}

function buildTraceDirectoryNode(filePath: string, existing?: FileTreeNode): FileTreeNode {
    const traceDirPath = getDocumentTraceDirPath(filePath);
    return {
        name: "traces",
        path: traceDirPath,
        isDirectory: true,
        isVirtual: true,
        children: existing?.children ?? [],
        loaded: existing?.loaded ?? false,
        expanded: existing?.expanded ?? false,
    };
}

function mergeTraceDirectoryNode(
    filePath: string,
    nextChildren: FileTreeNode[],
    existingChildren: FileTreeNode[],
    includeTraceNode: boolean,
): FileTreeNode[] {
    if (!includeTraceNode) return nextChildren;
    const traceDirPath = getDocumentTraceDirPath(filePath);
    if (nextChildren.some((child) => child.path === traceDirPath)) return nextChildren;
    const existing = existingChildren.find((child) => child.path === traceDirPath);
    return [...nextChildren, buildTraceDirectoryNode(filePath, existing)]
        .sort((a, b) => a.name.localeCompare(b.name));
}

function isExpandableTreeNode(node: FileTreeNode): boolean {
    return node.isDirectory || !!node.bundlePath;
}

function mergeVirtualChildrenWithExistingState(
    nextChildren: FileTreeNode[],
    existingChildren: FileTreeNode[],
    options?: {
        preserveExistingWhenNextEmpty?: boolean;
        preserveExistingUnmatched?: boolean;
    },
): FileTreeNode[] {
    if (nextChildren.length === 0 && options?.preserveExistingWhenNextEmpty && existingChildren.length > 0) {
        return existingChildren;
    }
    if (nextChildren.length === 0 || existingChildren.length === 0) return nextChildren;
    const existingByPath = new Map(existingChildren.map((child) => [child.path, child]));
    const existingByName = new Map(existingChildren.map((child) => [child.name, child]));
    const consumedExisting = new Set<FileTreeNode>();

    const merged = nextChildren.map((nextChild) => {
        const existing = existingByPath.get(nextChild.path) ?? existingByName.get(nextChild.name);
        if (existing) consumedExisting.add(existing);
        if (!existing || !isExpandableTreeNode(existing) || !isExpandableTreeNode(nextChild)) {
            return nextChild;
        }
        const existingLoaded = existing.loaded ?? false;
        return {
            ...nextChild,
            expanded: existing.expanded ?? nextChild.expanded,
            loaded: existingLoaded || (nextChild.loaded ?? false),
            children: existingLoaded
                ? mergeVirtualChildrenWithExistingState(
                    nextChild.children ?? [],
                    existing.children ?? [],
                    options,
                )
            : nextChild.children,
        };
    });
    if (!options?.preserveExistingUnmatched) return merged;
    const nextPaths = new Set(nextChildren.map((child) => child.path));
    const nextNames = new Set(nextChildren.map((child) => child.name));
    const extras = existingChildren.filter((existing) =>
        !consumedExisting.has(existing) &&
        !nextPaths.has(existing.path) &&
        !nextNames.has(existing.name)
    );
    return extras.length > 0 ? [...merged, ...extras] : merged;
}

async function loadAnnotationVirtualChildren(
    engine: WorkspaceEngine,
    annotationsPath: string,
): Promise<FileTreeNode[]> {
    const info = getAnnotationPathInfo(annotationsPath);
    if (!info) return [];
    if (isDocumentAnnotationsRootPath(annotationsPath)) {
        const annotations = await readAnnotationIndex(engine, `${annotationsPath}/index.json`);
        const counts = countAnnotationStatuses(annotations);
        return (["open", "resolved", "orphaned"] as const).map((status) => ({
            name: counts[status] > 0 ? `${status} · ${counts[status]}` : status,
            path: `${annotationsPath}/${status}`,
            isDirectory: true,
            isVirtual: true as const,
            children: [],
            loaded: false,
            expanded: false,
        }));
    }
    if (!isDocumentAnnotationStatusPath(annotationsPath) || !info.status) return [];
    const annotations = await readAnnotationIndex(engine, `${info.annotationsRoot}/index.json`);
    return annotations
        .filter((annotation) => annotation.status === info.status)
        .map((annotation) => ({
            name: annotationListLabel(annotation),
            path: `${annotationsPath}/${encodeURIComponent(annotation.id)}`,
            isDirectory: false,
            isVirtual: true as const,
            loaded: true,
            expanded: false,
        }));
}

async function readAnnotationIndex(
    engine: WorkspaceEngine,
    indexPath: string,
): Promise<PreviewAnnotation[]> {
    try {
        const { content } = await engine.workspaceReadFile({ path: indexPath });
        const parsed = JSON.parse(content) as {
            annotations?: PreviewAnnotation[];
        };
        return Array.isArray(parsed.annotations) ? parsed.annotations : [];
    } catch {
        return [];
    }
}

function countAnnotationStatuses(
    annotations: PreviewAnnotation[],
): Record<AnnotationStatus, number> {
    const counts = { open: 0, resolved: 0, orphaned: 0 };
    for (const annotation of annotations) {
        if (
            annotation.status === "open" ||
            annotation.status === "resolved" ||
            annotation.status === "orphaned"
        ) {
            counts[annotation.status] += 1;
        }
    }
    return counts;
}

function annotationListLabel(annotation: PreviewAnnotation): string {
    const target = annotation.target;
    const quote = "quote" in target
        ? target.quote
        : target.kind === "rendered-area"
            ? (
                target.shape === "area"
                    ? translate("editor", "annotation.areaComment")
                    : translate("editor", "annotation.pointComment")
            )
            : target.kind === "rendered-element"
                ? (target.label ?? translate("editor", "annotation.elementComment"))
            : annotation.id;
    const firstComment = annotation.thread?.comments?.[0]?.text ?? annotation.thread?.text;
    return truncateMiddle(firstComment || quote || annotation.id, 34);
}

function truncateMiddle(value: string, maxLength: number): string {
    if (value.length <= maxLength) return value;
    return `${value.slice(0, maxLength - 1)}…`;
}

function copyText(text: string) {
    navigator.clipboard.writeText(text).catch(() => {});
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === "object" && !Array.isArray(value);
}

function stringValue(record: Record<string, unknown>, key: string): string | undefined {
    const value = record[key];
    return typeof value === "string" && value.length > 0 ? value : undefined;
}

function normalizePersistedTraceRun(
    value: unknown,
    fallback: { traceId: string; filePath?: string },
): TraceRun | null {
    if (!isRecord(value)) return null;
    const traceId = stringValue(value, "traceId") ?? fallback.traceId;
    if (!traceId) return null;
    const startedAt = stringValue(value, "startedAt") ?? new Date().toISOString();
    const target = value.target === "unit" ? "unit" : "all";
    const status = value.status === "running" ||
        value.status === "completed" ||
        value.status === "failed" ||
        value.status === "cancelled" ||
        value.status === "skipped"
        ? value.status
        : "completed";
    const spans = Array.isArray(value.spans)
        ? value.spans.filter(isRecord) as unknown as TraceRun["spans"]
        : [];
    const events = Array.isArray(value.events)
        ? value.events.filter(isRecord) as unknown as TraceRun["events"]
        : [];
    return {
        traceId,
        executionId: stringValue(value, "executionId") ?? traceId,
        sessionId: stringValue(value, "sessionId") ?? `persisted:${traceId}`,
        filePath: stringValue(value, "filePath") ?? fallback.filePath,
        target,
        unitId: stringValue(value, "unitId"),
        unitIds: Array.isArray(value.unitIds)
            ? value.unitIds.filter((item): item is string => typeof item === "string")
            : undefined,
        action: stringValue(value, "action"),
        status,
        startedAt,
        completedAt: stringValue(value, "completedAt"),
        durationMs: typeof value.durationMs === "number" ? value.durationMs : undefined,
        baseRevision: typeof value.baseRevision === "number" ? value.baseRevision : undefined,
        currentRevision: typeof value.currentRevision === "number" ? value.currentRevision : undefined,
        eventCount: typeof value.eventCount === "number" ? value.eventCount : events.length,
        droppedCount: typeof value.droppedCount === "number" ? value.droppedCount : 0,
        spans,
        events,
    };
}

async function focusAnnotationPreview(info: NonNullable<ReturnType<typeof getAnnotationPathInfo>>) {
    if (!info.annotationId) return;
    await useEditorStore.getState().openFileFromPath(info.documentPath);
    window.setTimeout(() => {
        dispatchAnnotationSelect({
            documentPath: info.documentPath,
            annotationId: info.annotationId!,
            openPopover: true,
        });
    }, 0);
}

async function gotoAnnotationSourceFromExplorer(info: NonNullable<ReturnType<typeof getAnnotationPathInfo>>) {
    if (!info.annotationId) return;
    const document = await loadAnnotationDocument(info.documentPath);
    const annotation = document.annotations.find((item) => item.id === info.annotationId);
    if (!annotation) return;
    await useEditorStore.getState().openFileFromPath(info.documentPath);
    if (annotation.target.kind === "source-text") {
        window.dispatchEvent(
            new CustomEvent("magic-md:goto-line", {
                detail: {
                    line: annotation.target.range.startLine,
                    col: annotation.target.range.startColumn + 1,
                    flash: true,
                },
            }),
        );
        return;
    }
    dispatchAnnotationSelect({
        documentPath: info.documentPath,
        annotationId: annotation.id,
        openPopover: true,
    });
}

async function focusOutputWatchFromExplorer(documentPath: string, definitionId: string) {
    await useEditorStore.getState().openFileFromPath(documentPath);
    await useOutputWatchStore.getState().hydrateDocumentWatches({
        documentPath,
    });
    useOutputWatchStore.getState().selectPersistedWatch(documentPath, definitionId);
    useLogStore.getState().setActiveTab("watch");
}

async function ensureTraceLoadedFromExplorer(tracePath: string, traceId: string): Promise<boolean> {
    const store = useTraceStore.getState();
    if (store.runs.some((run) => run.traceId === traceId)) {
        store.selectTrace(traceId);
        return true;
    }
    const documentPath = getDocumentPathFromTracePath(tracePath) ?? undefined;
    try {
        const { content } = await getPlatformService().engine.workspaceReadFile({ path: tracePath });
        const parsed = JSON.parse(content);
        const run = normalizePersistedTraceRun(parsed, {
            traceId,
            filePath: documentPath,
        });
        if (!run) {
            useEditorStore.getState().setStatusMessage(
                translate("editor", "explorer.trace.invalidFile", { traceId }),
                4000,
            );
            return false;
        }
        useTraceStore.getState().importTrace(run);
        return true;
    } catch (error) {
        const message = error instanceof Error ? error.message : "Unknown error";
        useEditorStore.getState().setStatusMessage(
            translate("editor", "explorer.trace.loadFailed", { message }),
            4000,
        );
        return false;
    }
}

async function focusTraceFromExplorer(tracePath: string, traceId: string) {
    const documentPath = getDocumentPathFromTracePath(tracePath);
    if (documentPath) {
        await useEditorStore.getState().openFileFromPath(documentPath);
    }
    const loaded = await ensureTraceLoadedFromExplorer(tracePath, traceId);
    if (!loaded) return;
    useLogStore.getState().setActiveTab("trace");
}

async function saveTraceFromExplorer(traceId: string, tracePath: string, traceName: string) {
    const content = useTraceStore.getState().exportTrace(traceId);
    const editor = useEditorStore.getState();
    if (!content) {
        editor.setStatusMessage(translate("editor", "explorer.trace.notFound", { traceId }), 4000);
        return;
    }

    const dirIndex = tracePath.lastIndexOf("/");
    const traceDir = dirIndex > 0 ? tracePath.slice(0, dirIndex) : "";
    try {
        const engine = getPlatformService().engine;
        if (traceDir) {
            await engine.workspaceMkdir({ path: traceDir, recursive: true });
        }
        await engine.workspaceWriteFile({ path: tracePath, content });
        window.dispatchEvent(
            new CustomEvent(BUNDLE_REFRESH_EVENT, {
                detail: {
                    filePath: tracePath,
                    traceDir,
                    bundleDir: traceDir.replace(/\/traces$/, ""),
                } satisfies BundleRefreshDetail,
            }),
        );
        editor.setStatusMessage(translate("editor", "explorer.trace.saved", { name: traceName }), 4000);
    } catch (error) {
        const message = error instanceof Error ? error.message : "Unknown error";
        editor.setStatusMessage(translate("editor", "explorer.trace.saveFailed", { message }), 4000);
    }
}

async function deleteTraceFromExplorer(traceId: string, tracePath: string, traceName: string) {
    const ok = window.confirm(translate("editor", "explorer.trace.deleteConfirm", { name: traceName }));
    if (!ok) return;

    const dirIndex = tracePath.lastIndexOf("/");
    const traceDir = dirIndex > 0 ? tracePath.slice(0, dirIndex) : "";
    useTraceStore.getState().deleteTrace(traceId);
    try {
        await getPlatformService().engine.workspaceRemove({
            path: tracePath,
            recursive: false,
        });
    } catch {
        // The trace may only exist in memory until the user explicitly saves it.
    }
    window.dispatchEvent(
        new CustomEvent(BUNDLE_REFRESH_EVENT, {
            detail: {
                filePath: tracePath,
                traceDir,
                bundleDir: traceDir.replace(/\/traces$/, ""),
            } satisfies BundleRefreshDetail,
        }),
    );
    useEditorStore.getState().setStatusMessage(
        translate("editor", "explorer.trace.deleted", { name: traceName }),
        4000,
    );
}

async function updateAnnotationFromExplorer(
    info: NonNullable<ReturnType<typeof getAnnotationPathInfo>>,
    updater: (annotations: PreviewAnnotation[]) => PreviewAnnotation[],
) {
    const document = await loadAnnotationDocument(info.documentPath);
    const nextAnnotations = updater(document.annotations);
    await saveAnnotationDocument(info.documentPath, nextAnnotations);
    window.dispatchEvent(
        new CustomEvent(BUNDLE_REFRESH_EVENT, {
            detail: {
                filePath: info.documentPath,
                bundleDir: documentBundlePath(info.documentPath) ?? undefined,
                annotationDir: info.annotationsRoot,
            } satisfies BundleRefreshDetail,
        }),
    );
}

function revealInFolderUrl(filePath: string): string {
    const idx = filePath.lastIndexOf("/");
    const dir = idx > 0 ? filePath.slice(0, idx) : filePath;
    return `file://${dir.split("/").map((seg) => encodeURIComponent(seg)).join("/")}`;
}

function countInstances(parsedDocument: ParsedDocument | null | undefined): number {
    return (
        (parsedDocument?.definitions.size ?? 0) +
        (parsedDocument?.forDefinitions?.size ?? 0) +
        (parsedDocument?.contentDefinitions?.size ?? 0) +
        (parsedDocument?.imports?.length ?? 0) +
        (parsedDocument?.uses?.length ?? 0)
    );
}

function VirtualInlineInput({
    defaultValue,
    depth,
    indentBase,
    icon,
    onCommit,
    onCancel,
}: {
    defaultValue: string;
    depth: number;
    indentBase: number;
    icon: ReactNode;
    onCommit: (value: string) => Promise<void> | void;
    onCancel: () => void;
}) {
    const inputRef = useRef<HTMLInputElement>(null);
    const committingRef = useRef(false);

    useEffect(() => {
        const input = inputRef.current;
        if (!input) return;
        input.focus();
        const dotIndex = defaultValue.lastIndexOf(".");
        if (dotIndex > 0) input.setSelectionRange(0, dotIndex);
        else input.select();
    }, [defaultValue]);

    const commit = (value: string) => {
        if (committingRef.current) return;
        if (!value) {
            onCancel();
            return;
        }
        committingRef.current = true;
        void Promise.resolve(onCommit(value)).catch(() => {
            committingRef.current = false;
        });
    };

    const handleKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            event.preventDefault();
            commit(inputRef.current?.value.trim() ?? "");
        } else if (event.key === "Escape") {
            event.preventDefault();
            onCancel();
        }
    };

    return (
        <div
            className="flex items-center w-full text-xs py-0.5 pr-2"
            style={{ paddingLeft: `${indentBase + depth * 12 + 16}px` }}
        >
            <span className="mr-1.5 flex items-center shrink-0">{icon}</span>
            <input
                ref={inputRef}
                defaultValue={defaultValue}
                onKeyDown={handleKeyDown}
                onBlur={() => {
                    if (committingRef.current) return;
                    const value = inputRef.current?.value.trim();
                    if (value && value !== defaultValue) commit(value);
                    else onCancel();
                }}
                className="flex-1 min-w-0 h-5 bg-background border border-primary/50 rounded px-1 text-xs font-mono outline-none focus:border-primary"
            />
        </div>
    );
}

function snapshotNote(snapshot: SnapshotMeta): string | null {
    if (snapshot.tag) return snapshot.tag;
    if (snapshot.message) return snapshot.message;
    return null;
}

function formatRelative(iso: string): string {
    const timestamp = Date.parse(iso);
    if (Number.isNaN(timestamp)) return iso;
    const diffMs = Math.max(0, Date.now() - timestamp);
    const seconds = Math.floor(diffMs / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    return new Date(timestamp).toLocaleDateString();
}

function triggerLabel(trigger: SnapshotMeta["trigger"]): string {
    switch (trigger) {
        case "manual":
            return "manual";
        case "on-save":
            return "save";
        case "auto-pre-restore":
            return "restore";
    }
}

function VirtualBundleNode({
    node,
    depth,
    indentBase,
    onOpenFile,
    parsedDocument,
    persistExpansion = false,
}: {
    node: FileTreeNode;
    depth: number;
    indentBase: number;
    onOpenFile: (path: string) => void;
    parsedDocument?: ParsedDocument | null;
    persistExpansion?: boolean;
}) {
    const t = useT("editor");
    const [expanded, setExpanded] = useState(node.expanded ?? false);
    const [children, setChildren] = useState<FileTreeNode[]>(node.children ?? []);
    const [loaded, setLoaded] = useState(node.loaded ?? false);
    const [ctxMenu, setCtxMenu] = useState<{
        open: boolean;
        x: number;
        y: number;
    }>({ open: false, x: 0, y: 0 });
    const [deleted, setDeleted] = useState(false);
    const [creating, setCreating] = useState<null | { type: "file" | "folder" }>(null);
    const [renaming, setRenaming] = useState(false);
    const refreshHistory = useHistoryStore((state) => state.refresh);
    const takeSnapshot = useHistoryStore((state) => state.take);
    const toggleTreeNode = useEditorStore((state) => state.toggleTreeNode);
    const traceRuns = useTraceStore((state) => state.runs);

    const isExpandable = node.isDirectory || !!node.bundlePath;
    const isTraceTreeNode = !!node.isVirtual && isDocumentTraceDirPath(node.path);
    const isTraceItemNode = !!node.isVirtual && isDocumentTraceItemPath(node.path);
    const isTraceEmptyNode = !!node.isVirtual && isDocumentTraceEmptyPath(node.path);
    const traceItemId = isTraceItemNode ? getTraceIdFromPath(node.path) : null;
    const isPublishBundleNode =
        !!node.isVirtual &&
        !!node.isDirectory &&
        /\/[^/]+\.magic-md\/publish$/.test(node.path);
    const isHistoryBundleNode =
        !!node.isVirtual &&
        !!node.isDirectory &&
        /\.magic-md\/history$/.test(node.path);
    const isOutputsBundleNode =
        !!node.isVirtual &&
        !!node.isDirectory &&
        /\.magic-md\/outputs$/.test(node.path);
    const isConfigsTreeNode =
        !!node.isVirtual &&
        /\.magic-md\/configs(?:\/|$)/.test(node.path);
    const isAnnotationsTreeNode = !!node.isVirtual && isDocumentAnnotationsPath(node.path);
    const isAnnotationsRootNode = !!node.isVirtual && isDocumentAnnotationsRootPath(node.path);
    const isAnnotationItemNode = !!node.isVirtual && isDocumentAnnotationItemPath(node.path);
    const annotationInfo = useMemo(() => getAnnotationPathInfo(node.path), [node.path]);
    const isOutputWatchesTreeNode = !!node.isVirtual && isDocumentWatchesPath(node.path);
    const isOutputWatchItemNode = !!node.isVirtual && isDocumentWatchItemPath(node.path);
    const outputWatchItemId = isOutputWatchItemNode ? getOutputWatchItemIdFromPath(node.path) : null;
    const outputWatchDocumentPath = isOutputWatchItemNode
        ? getDocumentPathFromOutputWatchPath(node.path)
        : null;
    const isAssetsTreeNode = !!node.isVirtual && isDocumentAssetsPath(node.path);
    const isAssetsMarkdownFile = isAssetsTreeNode && !node.isDirectory && /\.md$/i.test(node.name);
    const assetRef = !node.isDirectory ? getAssetRefFromPath(node.path) : null;
    const assetRelativePath = getAssetRelativePathFromPath(node.path);
    const historyTargetPath = isHistoryBundleNode
        ? node.path.replace(/\.magic-md\/history$/, ".md")
        : null;
    const publishTargetPath = isPublishBundleNode
        ? node.path.replace(/\.magic-md\/publish$/, ".md")
        : null;
    const outputsTargetPath = isOutputsBundleNode
        ? node.path.replace(/\.magic-md\/outputs$/, ".md")
        : null;

    useEffect(() => {
        if (!isTraceTreeNode) return;
        let cancelled = false;
        const documentPath = getDocumentPathFromTracePath(node.path);
        const runs = traceRuns.filter((run) =>
            traceRunBelongsToDocument(run, documentPath),
        );
        let engine: WorkspaceEngine | null = null;
        try {
            engine = getPlatformService().engine;
        } catch {
            engine = null;
        }
        if (!engine) {
            setChildren(buildTraceVirtualChildren(node.path, runs));
            setLoaded(true);
            return;
        }
        safeReadDir(engine, node.path).then((entries) => {
            if (cancelled) return;
            setChildren(buildTraceVirtualChildren(
                node.path,
                runs,
                traceIdsFromEntries(node.path, entries),
            ));
            setLoaded(true);
        });
        return () => {
            cancelled = true;
        };
    }, [isTraceTreeNode, node.path, traceRuns]);

    const ensureLoaded = useCallback(async (options?: { force?: boolean }) => {
        if (loaded && !options?.force) return;
        const nextChildren = await loadVirtualChildren(node);
        setChildren((current) => mergeVirtualChildrenWithExistingState(nextChildren, current));
        setLoaded(true);
    }, [loaded, node]);

    useEffect(() => {
        if (!persistExpansion) return;
        setChildren((current) => mergeVirtualChildrenWithExistingState(
            node.children ?? [],
            current,
            {
                preserveExistingWhenNextEmpty: true,
                preserveExistingUnmatched: true,
            },
        ));
        setLoaded((current) => current || (node.loaded ?? false));
    }, [
        node.children,
        node.loaded,
        persistExpansion,
    ]);

    const toggle = useCallback(() => {
        if (traceItemId) {
            void focusTraceFromExplorer(node.path, traceItemId);
            return;
        }
        if (isTraceEmptyNode) return;
        if (isAnnotationItemNode && annotationInfo?.annotationId) {
            void focusAnnotationPreview(annotationInfo);
            return;
        }
        if (outputWatchItemId && outputWatchDocumentPath) {
            void focusOutputWatchFromExplorer(outputWatchDocumentPath, outputWatchItemId);
            return;
        }
        if (!isExpandable) {
            onOpenFile(node.path);
            return;
        }
        if (persistExpansion) {
            setExpanded((next) => {
                const expandedNext = !next;
                if (expandedNext) {
                    void ensureLoaded({ force: loaded && children.length === 0 });
                }
                return expandedNext;
            });
            void toggleTreeNode(node.path);
            return;
        }
        setExpanded((next) => {
            const expandedNext = !next;
            if (expandedNext) {
                void ensureLoaded({ force: loaded && children.length === 0 });
            }
            return expandedNext;
        });
    }, [
        ensureLoaded,
        annotationInfo,
        isAnnotationItemNode,
        outputWatchDocumentPath,
        outputWatchItemId,
        isExpandable,
        node.path,
        onOpenFile,
        persistExpansion,
        toggleTreeNode,
        loaded,
        children.length,
        isTraceEmptyNode,
        traceItemId,
    ]);

    const refreshChildren = useCallback(async (options?: { expand?: boolean; notify?: boolean }) => {
        try {
            const nextChildren = await loadVirtualChildren(node);
            setChildren((current) => mergeVirtualChildrenWithExistingState(nextChildren, current));
            setLoaded(true);
            if (options?.expand ?? true) {
                setExpanded(true);
            }
            if (options?.notify ?? true) {
                useEditorStore.getState().setStatusMessage(t("explorer.refreshed", { name: node.name }));
            }
        } catch (error) {
            const message = error instanceof Error ? error.message : t("common.unknown");
            if (options?.notify ?? true) {
                useEditorStore.getState().setStatusMessage(t("explorer.refreshFailed", { message }));
            }
        }
    }, [node, t]);

    const createAssetEntry = useCallback(async (type: "file" | "folder", name: string) => {
        const finalName = name.trim();
        if (!finalName || finalName.includes("/") || finalName.includes("\\")) {
            useEditorStore.getState().setStatusMessage(
                type === "file"
                    ? t("explorer.assets.invalidFileName")
                    : t("explorer.assets.invalidFolderName"),
            );
            return;
        }
        const targetPath = `${node.path}/${finalName}`;
        try {
            const engine = getPlatformService().engine;
            const { exists } = await engine.workspaceExists({ path: targetPath });
            if (exists) {
                useEditorStore.getState().setStatusMessage(
                    type === "file"
                        ? t("explorer.fileExists", { name: finalName })
                        : t("explorer.folderExists", { name: finalName }),
                );
                return;
            }
            if (type === "file") {
                await engine.workspaceWriteFile({ path: targetPath, content: "" });
            } else {
                await engine.workspaceMkdir({ path: targetPath, recursive: true });
            }
            await refreshChildren();
            useEditorStore.getState().setStatusMessage(
                type === "file"
                    ? t("explorer.assets.fileCreated", { name: finalName })
                    : t("explorer.assets.folderCreated", { name: finalName }),
            );
        } catch (error) {
            const message = error instanceof Error ? error.message : t("common.unknown");
            useEditorStore.getState().setStatusMessage(
                type === "file"
                    ? t("explorer.assets.createFileFailed", { message })
                    : t("explorer.assets.createFolderFailed", { message }),
            );
        } finally {
            setCreating(null);
        }
    }, [node.path, refreshChildren, t]);

    const renameAssetEntry = useCallback(async (name: string) => {
        const nextName = name.trim();
        if (!nextName || nextName === node.name) {
            setRenaming(false);
            return;
        }
        if (nextName.includes("/") || nextName.includes("\\")) {
            useEditorStore.getState().setStatusMessage(t("explorer.assets.invalidFileName"));
            setRenaming(false);
            return;
        }
        const renamed = await useEditorStore.getState().renameNode(node.path, nextName);
        setRenaming(false);
        if (!renamed) return;
        const parentDir = node.path.slice(0, node.path.lastIndexOf("/"));
        if (parentDir) {
            window.dispatchEvent(
                new CustomEvent(BUNDLE_REFRESH_EVENT, {
                    detail: {
                        filePath: node.path,
                        bundleDir: parentDir,
                    } satisfies BundleRefreshDetail,
                }),
            );
        }
        setDeleted(true);
    }, [node.name, node.path, t]);

    const takeHistorySnapshot = useCallback(async () => {
        if (!historyTargetPath) return;
        const editor = useEditorStore.getState();
        const openTab = editor.tabs.find((tab) => tab.filePath === historyTargetPath);
        editor.setStatusMessage(t("explorer.history.snapshotTaking"), undefined);
        const snapshot = await takeSnapshot({
            filePath: historyTargetPath,
            trigger: "manual",
            content: openTab?.content,
        });
        if (!snapshot) {
            editor.setStatusMessage(t("explorer.history.snapshotFailed"), 4000);
            return;
        }
        await refreshHistory(historyTargetPath);
        window.dispatchEvent(
            new CustomEvent(BUNDLE_REFRESH_EVENT, {
                detail: {
                    filePath: historyTargetPath,
                    bundleDir: documentBundlePath(historyTargetPath) ?? undefined,
                } satisfies BundleRefreshDetail,
            }),
        );
        setExpanded(true);
        editor.setStatusMessage(t("explorer.history.snapshotCreated"), 4000);
    }, [historyTargetPath, refreshHistory, takeSnapshot, t]);

    useEffect(() => {
        if (typeof window === "undefined") return;
        const handler = (event: Event) => {
            const detail = (event as CustomEvent<BundleRefreshDetail>).detail;
            if (!detail) return;
            if (
                detail.bundleDir === node.path ||
                (node.isDirectory && detail.bundleDir?.startsWith(`${node.path}/`)) ||
                detail.publishDir === node.path ||
                detail.filePath === publishTargetPath ||
                detail.filePath === historyTargetPath ||
                detail.filePath === outputsTargetPath ||
                (!!detail.bundleDir && node.path.startsWith(`${detail.bundleDir}/configs`))
                || (!!detail.bundleDir && node.path.startsWith(`${detail.bundleDir}/annotations`))
                || (!!detail.bundleDir && node.path.startsWith(`${detail.bundleDir}/watches`))
                || (!!detail.bundleDir && node.path.startsWith(`${detail.bundleDir}/traces`))
                || (!!detail.annotationDir && node.path.startsWith(detail.annotationDir))
                || (!!detail.traceDir && node.path.startsWith(detail.traceDir))
            ) {
                void refreshChildren({ expand: false, notify: false });
            }
        };
        window.addEventListener(BUNDLE_REFRESH_EVENT, handler);
        return () => window.removeEventListener(BUNDLE_REFRESH_EVENT, handler);
    }, [historyTargetPath, node.isDirectory, node.path, outputsTargetPath, publishTargetPath, refreshChildren]);

    const contextItems = useMemo<ContextMenuItem[]>(() => {
        const items: ContextMenuItem[] = [];
        if (isAssetsTreeNode) {
            if (node.isDirectory) {
                items.push({
                    key: "assets-new-file",
                    label: t("explorer.assets.newFile"),
                    icon: <FilePlus size={14} />,
                    onClick: () => {
                        setExpanded(true);
                        if (!loaded) void ensureLoaded();
                        setCreating({ type: "file" });
                    },
                });
                items.push({
                    key: "assets-new-folder",
                    label: t("explorer.assets.newFolder"),
                    icon: <FolderPlus size={14} />,
                    onClick: () => {
                        setExpanded(true);
                        if (!loaded) void ensureLoaded();
                        setCreating({ type: "folder" });
                    },
                });
                items.push({
                    key: "assets-import",
                    label: t("explorer.assets.importFile"),
                    icon: <Upload size={14} />,
                    onClick: async () => {
                        const sourcePath = await showFilePickerDialog();
                        if (!sourcePath) return;
                        const name = sourcePath.split("/").pop() ?? "asset";
                        try {
                            await getPlatformService().engine.workspaceCopy({
                                from: sourcePath,
                                to: `${node.path}/${name}`,
                                autoRename: true,
                            });
                            await refreshChildren();
                            useEditorStore.getState().setStatusMessage(t("explorer.assets.imported", { name }));
                        } catch (error) {
                            const message = error instanceof Error ? error.message : t("common.unknown");
                            useEditorStore.getState().setStatusMessage(t("explorer.assets.importFailed", { message }));
                        }
                    },
                });
            }
            if (assetRelativePath && node.isDirectory) {
                items.push({
                    key: "assets-copy-prefix",
                    label: t("explorer.assets.copyRefPrefix"),
                    icon: <Link size={14} />,
                    separator: true,
                    onClick: () => copyText(`$ref{assets/${assetRelativePath}/}`),
                });
            }
            if (assetRef) {
                items.push({
                    key: "assets-copy-ref",
                    label: t("explorer.assets.copyRef"),
                    icon: <Link size={14} />,
                    separator: true,
                    onClick: () => copyText(assetRef),
                });
                items.push({
                    key: "assets-copy-markdown",
                    label: t("explorer.assets.copyMarkdown"),
                    icon: <Clipboard size={14} />,
                    onClick: () => {
                        const isImage = /\.(png|jpe?g|gif|webp|svg|avif)$/i.test(node.name);
                        copyText(isImage ? `![${node.name}](${assetRef})` : `[${node.name}](${assetRef})`);
                    },
                });
            }
            items.push({
                key: "assets-rename",
                label: t("common.rename"),
                icon: <Link size={14} />,
                separator: true,
                onClick: () => setRenaming(true),
            });
            items.push({
                key: "assets-delete",
                label: t("common.delete"),
                icon: <Trash2 size={14} />,
                onClick: async () => {
                    const deleted = await useEditorStore.getState().deleteNode(
                        node.path,
                        node.isDirectory,
                        node.bundlePath,
                    );
                    if (deleted) setDeleted(true);
                },
            });
            items.push({
                key: "assets-reveal-in-finder",
                label: t("explorer.revealInFolder"),
                icon: <FolderOpen size={14} />,
                separator: true,
                disabled: !getPlatformService().openExternal,
                onClick: async () => {
                    const platform = getPlatformService();
                    await platform.openExternal?.(revealInFolderUrl(node.path));
                },
            });
        }
        if (publishTargetPath) {
            items.push({
                key: "publish-file",
                label: t("publish.document"),
                icon: <Upload size={14} />,
                separator: true,
                onClick: async () => {
                    await showDocumentPublishDialog(publishTargetPath);
                },
            });
            if (hasKnownDocumentPublishPreview(publishTargetPath)) {
                items.push({
                    key: "preview-publish",
                    label: t("menu.previewPublish"),
                    icon: <Eye size={14} />,
                    onClick: async () => {
                        await previewDocumentPublish(publishTargetPath);
                    },
                });
            }
            items.push({
                key: "refresh-publish",
                label: t("common.refresh"),
                icon: <RefreshCw size={14} />,
                onClick: () => {
                    void refreshChildren();
                },
            });
            items.push({
                key: "delete-publish",
                label: t("explorer.publish.deleteOutput"),
                icon: <Trash2 size={14} />,
                separator: true,
                onClick: async () => {
                    const ok = window.confirm(
                        t("explorer.publish.confirmDeleteDocumentOutput", { name: node.name }),
                    );
                    if (!ok) return;
                    try {
                        await getPlatformService().engine.workspaceRemove({
                            path: node.path,
                            recursive: true,
                        });
                        setChildren([]);
                        setLoaded(true);
                        setExpanded(false);
                        usePublishStore.getState().clearFileTask(publishTargetPath);
                        window.dispatchEvent(
                            new CustomEvent(BUNDLE_REFRESH_EVENT, {
                                detail: {
                                    filePath: publishTargetPath,
                                    bundleDir: documentBundlePath(publishTargetPath) ?? undefined,
                                    publishDir: node.path,
                                } satisfies BundleRefreshDetail,
                            }),
                        );
                        setDeleted(true);
                        useEditorStore.getState().setStatusMessage(t("explorer.publish.outputDeleted"));
                    } catch (error) {
                        const message = error instanceof Error ? error.message : t("common.unknown");
                        useEditorStore.getState().setStatusMessage(t("explorer.publish.outputDeleteFailed", { message }));
                    }
                },
            });
        }
        if (historyTargetPath) {
            items.push({
                key: "history-snapshot",
                label: t("explorer.history.createSnapshot"),
                icon: <Clock size={14} />,
                separator: true,
                onClick: takeHistorySnapshot,
            });
            items.push({
                key: "history-refresh",
                label: t("common.refresh"),
                icon: <RefreshCw size={14} />,
                onClick: () => {
                    refreshHistory(historyTargetPath).catch(() => {});
                },
            });
        }
        if (outputsTargetPath) {
            items.push({
                key: "outputs-refresh",
                label: t("common.refresh"),
                icon: <RefreshCw size={14} />,
                onClick: () => {
                    void refreshChildren();
                },
            });
            items.push({
                key: "outputs-delete",
                label: t("explorer.outputs.delete"),
                icon: <Trash2 size={14} />,
                separator: true,
                onClick: async () => {
                    const ok = window.confirm(
                        t("explorer.outputs.confirmDelete", { name: node.name }),
                    );
                    if (!ok) return;
                    try {
                        await getPlatformService().engine.workspaceRemove({
                            path: node.path,
                            recursive: true,
                        });
                        setChildren([]);
                        setLoaded(true);
                        setExpanded(false);
                        setDeleted(true);
                        useEditorStore.getState().setStatusMessage(t("explorer.outputs.deleted"));
                    } catch (error) {
                        const message = error instanceof Error ? error.message : t("common.unknown");
                        useEditorStore.getState().setStatusMessage(t("explorer.outputs.deleteFailed", { message }));
                    }
                },
            });
            items.push({
                key: "outputs-reveal-in-finder",
                label: t("explorer.revealInFolder"),
                icon: <FolderOpen size={14} />,
                disabled: !getPlatformService().openExternal,
                onClick: async () => {
                    const platform = getPlatformService();
                    await platform.openExternal?.(revealInFolderUrl(node.path));
                },
            });
        }
        if (isConfigsTreeNode) {
            if (node.isDirectory) {
                items.push({
                    key: "configs-refresh",
                    label: t("common.refresh"),
                    icon: <RefreshCw size={14} />,
                    onClick: () => {
                        void refreshChildren();
                    },
                });
            }
            items.push({
                key: "configs-delete",
                label: getConfigsDeleteLabel(node.path, node.isDirectory, t),
                icon: <Trash2 size={14} />,
                separator: true,
                onClick: async () => {
                    const ok = window.confirm(
                        getConfigsDeleteConfirm(node.path, node.name, node.isDirectory, t),
                    );
                    if (!ok) return;
                    try {
                        await getPlatformService().engine.workspaceRemove({
                            path: node.path,
                            recursive: node.isDirectory,
                        });
                        const bundleDir = getBundleDirFromConfigsPath(node.path);
                        if (bundleDir) {
                            window.dispatchEvent(
                                new CustomEvent(BUNDLE_REFRESH_EVENT, {
                                    detail: {
                                        bundleDir,
                                    } satisfies BundleRefreshDetail,
                                }),
                            );
                        }
                        setDeleted(true);
                        useEditorStore.getState().setStatusMessage(t("explorer.configs.deleted"));
                    } catch (error) {
                        const message = error instanceof Error ? error.message : t("common.unknown");
                        useEditorStore.getState().setStatusMessage(t("explorer.configs.deleteFailed", { message }));
                    }
                },
            });
            items.push({
                key: "configs-reveal-in-finder",
                label: t("explorer.revealInFolder"),
                icon: <FolderOpen size={14} />,
                disabled: !getPlatformService().openExternal,
                onClick: async () => {
                    const platform = getPlatformService();
                    await platform.openExternal?.(revealInFolderUrl(node.path));
                },
            });
        }
        if (isAnnotationsTreeNode) {
            if (isAnnotationItemNode && annotationInfo?.annotationId) {
                items.push(
                    {
                        key: "annotation-preview",
                        label: t("annotation.locatePreview"),
                        icon: <Eye size={14} />,
                        separator: items.length > 0,
                        onClick: () => {
                            void focusAnnotationPreview(annotationInfo);
                        },
                    },
                    {
                        key: "annotation-source",
                        label: t("annotation.goToSource"),
                        icon: <Code2 size={14} />,
                        onClick: () => {
                            void gotoAnnotationSourceFromExplorer(annotationInfo);
                        },
                    },
                    {
                        key: "annotation-resolve",
                        label: t("annotation.markResolved"),
                        icon: <Check size={14} />,
                        separator: true,
                        onClick: async () => {
                            const now = new Date().toISOString();
                            await updateAnnotationFromExplorer(annotationInfo, (annotations) =>
                                annotations.map((annotation) =>
                                    annotation.id === annotationInfo.annotationId
                                        ? {
                                            ...annotation,
                                            status: "resolved",
                                            updatedAt: now,
                                        }
                                        : annotation,
                                ),
                            );
                            useEditorStore.getState().setStatusMessage(t("annotation.status.markedResolved"));
                        },
                    },
                    ...(annotationInfo.status === "orphaned"
                        ? [
                            {
                                key: "annotation-retry",
                                label: t("annotation.retryLocate"),
                                icon: <RefreshCw size={14} />,
                                onClick: () => {
                                    void focusAnnotationPreview(annotationInfo);
                                    useEditorStore.getState().setStatusMessage(t("annotation.status.retryingLocate"));
                                },
                            },
                            {
                                key: "annotation-reopen",
                                label: t("annotation.reopen"),
                                icon: <Check size={14} />,
                                onClick: async () => {
                                    const now = new Date().toISOString();
                                    await updateAnnotationFromExplorer(annotationInfo, (annotations) =>
                                        annotations.map((annotation) =>
                                            annotation.id === annotationInfo.annotationId
                                                ? {
                                                    ...annotation,
                                                    status: "open",
                                                    statusBeforeOrphaned: undefined,
                                                    updatedAt: now,
                                                }
                                                : annotation,
                                        ),
                                    );
                                    useEditorStore.getState().setStatusMessage(t("annotation.status.reopened"));
                                },
                            },
                            {
                                key: "annotation-rebind",
                                label: t("annotation.rebindToSelection"),
                                icon: <Link size={14} />,
                                onClick: () => {
                                    void focusAnnotationPreview(annotationInfo);
                                    window.setTimeout(() => {
                                        dispatchAnnotationRebind({
                                            documentPath: annotationInfo.documentPath,
                                            annotationId: annotationInfo.annotationId!,
                                        });
                                    }, 0);
                                },
                            },
                        ] satisfies ContextMenuItem[]
                        : []),
                    {
                        key: "annotation-delete",
                        label: t("annotation.delete"),
                        icon: <Trash2 size={14} />,
                        onClick: async () => {
                            const ok = window.confirm(
                                t("annotation.confirmDelete", { name: node.name }),
                            );
                            if (!ok) return;
                            await updateAnnotationFromExplorer(annotationInfo, (annotations) =>
                                annotations.filter((annotation) => annotation.id !== annotationInfo.annotationId),
                            );
                            setDeleted(true);
                            useEditorStore.getState().setStatusMessage(t("annotation.status.deleted"));
                        },
                    },
                );
            } else if (isDocumentAnnotationStatusPath(node.path) && annotationInfo?.status) {
                if (annotationInfo.status !== "resolved") {
                    items.push({
                        key: "annotations-status-resolve-all",
                        label: t("annotation.resolveAllInGroup"),
                        icon: <Check size={14} />,
                        separator: items.length > 0,
                        onClick: async () => {
                            const now = new Date().toISOString();
                            await updateAnnotationFromExplorer(annotationInfo, (annotations) =>
                                annotations.map((annotation) =>
                                    annotation.status === annotationInfo.status
                                        ? {
                                            ...annotation,
                                            status: "resolved",
                                            updatedAt: now,
                                        }
                                        : annotation,
                                ),
                            );
                            await refreshChildren({ notify: false });
                            useEditorStore.getState().setStatusMessage(t("annotation.status.groupResolved"));
                        },
                    });
                }
                items.push({
                    key: "annotations-status-delete-all",
                    label: t("annotation.deleteGroup"),
                    icon: <Trash2 size={14} />,
                    separator: true,
                    onClick: async () => {
                        const ok = window.confirm(
                            t("annotation.confirmDeleteGroup", { name: node.name }),
                        );
                        if (!ok) return;
                        await updateAnnotationFromExplorer(annotationInfo, (annotations) =>
                            annotations.filter((annotation) => annotation.status !== annotationInfo.status),
                        );
                        await refreshChildren({ notify: false });
                        useEditorStore.getState().setStatusMessage(t("annotation.status.groupDeleted"));
                    },
                });
            } else {
                items.push({
                    key: "annotations-refresh",
                    label: t("common.refresh"),
                    icon: <RefreshCw size={14} />,
                    separator: items.length > 0,
                    onClick: () => {
                        void refreshChildren();
                    },
                });
            }
            if (isAnnotationsRootNode) {
                items.push(
                    {
                        key: "annotations-delete",
                        label: t("annotation.deletePackage"),
                        icon: <Trash2 size={14} />,
                        separator: true,
                        onClick: async () => {
                            const ok = window.confirm(
                                t("annotation.confirmDeletePackage", { name: node.name }),
                            );
                            if (!ok) return;
                            try {
                                await getPlatformService().engine.workspaceRemove({
                                    path: node.path,
                                    recursive: true,
                                });
                                const bundleDir = getBundleDirFromAnnotationsPath(node.path);
                                if (bundleDir) {
                                    window.dispatchEvent(
                                        new CustomEvent(BUNDLE_REFRESH_EVENT, {
                                            detail: {
                                                bundleDir,
                                                annotationDir: node.path,
                                            } satisfies BundleRefreshDetail,
                                        }),
                                    );
                                }
                                setDeleted(true);
                                useEditorStore.getState().setStatusMessage(t("annotation.status.packageDeleted"));
                            } catch (error) {
                                const message = error instanceof Error ? error.message : t("common.unknown");
                                useEditorStore.getState().setStatusMessage(t("annotation.status.packageDeleteFailed", { message }));
                            }
                        },
                    },
                    {
                        key: "annotations-reveal-in-finder",
                        label: t("explorer.revealInFolder"),
                        icon: <FolderOpen size={14} />,
                        disabled: !getPlatformService().openExternal,
                        onClick: async () => {
                            const platform = getPlatformService();
                            await platform.openExternal?.(revealInFolderUrl(node.path));
                        },
                    },
                );
            }
        }
        if (isOutputWatchesTreeNode && node.isDirectory) {
            items.push({
                key: "watches-refresh",
                label: t("common.refresh"),
                icon: <RefreshCw size={14} />,
                separator: items.length > 0,
                onClick: () => {
                    void refreshChildren();
                },
            });
        }
        if (isOutputWatchItemNode && outputWatchDocumentPath && outputWatchItemId) {
            items.push({
                key: "watch-open",
                label: t("watch.showInWatches"),
                icon: <Eye size={14} />,
                separator: items.length > 0,
                onClick: () => {
                    void focusOutputWatchFromExplorer(outputWatchDocumentPath, outputWatchItemId);
                },
            });
            items.push({
                key: "watch-delete",
                label: t("common.delete"),
                icon: <Trash2 size={14} />,
                separator: true,
                onClick: async () => {
                    await useOutputWatchStore.getState().removePersistedWatch(
                        outputWatchDocumentPath,
                        outputWatchItemId,
                    );
                    setDeleted(true);
                },
            });
        }
        if (isTraceItemNode && traceItemId) {
            items.push({
                key: "trace-open",
                label: t("explorer.trace.showInTrace"),
                icon: <Activity size={14} />,
                separator: items.length > 0,
                onClick: () => {
                    void focusTraceFromExplorer(node.path, traceItemId);
                },
            });
            items.push({
                key: "trace-save",
                label: t("explorer.trace.save"),
                icon: <Save size={14} />,
                onClick: () => {
                    void saveTraceFromExplorer(traceItemId, node.path, node.name);
                },
            });
            items.push({
                key: "trace-copy-json",
                label: t("explorer.trace.copyJson"),
                icon: <Clipboard size={14} />,
                onClick: () => {
                    const content = useTraceStore.getState().exportTrace(traceItemId);
                    if (content) copyText(content);
                },
            });
            items.push({
                key: "trace-delete",
                label: t("explorer.trace.delete"),
                icon: <Trash2 size={14} />,
                separator: true,
                onClick: () => {
                    void deleteTraceFromExplorer(traceItemId, node.path, node.name);
                },
            });
        }
        if (
            !node.isDirectory &&
            !isAnnotationItemNode &&
            !isOutputWatchItemNode &&
            !isTraceItemNode &&
            !isTraceEmptyNode
        ) {
            items.push({
                key: "open",
                label: t("menu.openFile"),
                icon: <File size={14} />,
                onClick: () => onOpenFile(node.path),
            });
        }
        items.push(
            {
                key: "copy-path",
                label: t("menu.copyPath"),
                icon: <Clipboard size={14} />,
                separator: items.length > 0,
                onClick: () => copyText(node.path),
            },
            {
                key: "copy-relative",
                label: t("menu.copyRelativePath"),
                icon: <Link size={14} />,
                onClick: () => copyText(relativePath(node.path)),
            },
        );
        items.push(
            ...buildExtensionContextMenuItems(buildFileNodeMenuContext(node), {
                firstSeparator: items.length > 0,
            }),
        );
        return items;
    }, [
        historyTargetPath,
        node.isDirectory,
        node.isVirtual,
        node.name,
        node.path,
        onOpenFile,
        outputsTargetPath,
        publishTargetPath,
        refreshChildren,
        refreshHistory,
        takeHistorySnapshot,
        takeSnapshot,
        isAssetsTreeNode,
        assetRef,
        assetRelativePath,
        isConfigsTreeNode,
        isAnnotationsTreeNode,
        isAnnotationsRootNode,
        isAnnotationItemNode,
        annotationInfo,
        isOutputWatchesTreeNode,
        isOutputWatchItemNode,
        isTraceItemNode,
        traceItemId,
        isTraceEmptyNode,
        outputWatchDocumentPath,
        outputWatchItemId,
        t,
    ]);

    if (deleted) return null;

    if (renaming) {
        return (
            <VirtualInlineInput
                defaultValue={node.name}
                depth={depth}
                indentBase={indentBase}
                icon={node.isDirectory
                    ? getVirtualBundleIcon(node, parsedDocument)
                    : getFileIcon(node.name, 14)}
                onCommit={renameAssetEntry}
                onCancel={() => setRenaming(false)}
            />
        );
    }

    if (isAssetsMarkdownFile) {
        return (
            <DocumentFileTreeNode
                filePath={node.path}
                name={node.name}
                depth={depth}
                indentBase={indentBase}
                bundlePath={node.bundlePath ?? null}
                initialBundleChildren={node.children}
                initialBundleLoaded={node.loaded}
                initialExpanded={node.expanded}
                persistBundleExpansion={persistExpansion}
                contextItems={contextItems}
                onOpenFile={onOpenFile}
            />
        );
    }

    return (
        <>
            <button
                type="button"
                onClick={toggle}
                onContextMenu={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setCtxMenu({ open: true, x: event.clientX, y: event.clientY });
                }}
                data-explorer-node-path={node.path}
                className="flex items-center w-full text-left text-xs py-1 pr-2 rounded transition-colors text-foreground/70 hover:bg-muted/60 opacity-70"
                style={{ paddingLeft: `${indentBase + depth * 12}px` }}
                title={node.path}
            >
                {isExpandable ? (
                    <span className="w-4 h-4 flex items-center justify-center shrink-0">
                        {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                    </span>
                ) : (
                    <span className="w-4 shrink-0" />
                )}
                <span className="mr-1.5 flex items-center">
                    {getVirtualBundleIcon(node, parsedDocument)}
                </span>
                <span className="truncate">{node.name}</span>
            </button>

            {expanded && historyTargetPath && (
                <HistorySnapshotSection
                    filePath={historyTargetPath}
                    historyDir={node.path}
                    depth={depth + 1}
                    indentBase={indentBase}
                    onOpenFile={onOpenFile}
                />
            )}

            {expanded && !historyTargetPath && children.length > 0 && (
                <div>
                    {creating && (
                        <VirtualInlineInput
                            defaultValue={creating.type === "file" ? "untitled.txt" : "new-folder"}
                            depth={depth + 1}
                            indentBase={indentBase}
                            icon={creating.type === "file"
                                ? getFileIcon("untitled.txt", 14)
                                : <Package size={14} className="text-muted-foreground" />}
                            onCommit={(name) => createAssetEntry(creating.type, name)}
                            onCancel={() => setCreating(null)}
                        />
                    )}
                    {children.map((child) => (
                        <VirtualBundleNode
                            key={child.path}
                            node={child}
                            depth={depth + 1}
                            indentBase={indentBase}
                            onOpenFile={onOpenFile}
                            parsedDocument={parsedDocument}
                        />
                    ))}
                </div>
            )}
            {expanded && !historyTargetPath && children.length === 0 && creating && (
                <div>
                    <VirtualInlineInput
                        defaultValue={creating.type === "file" ? "untitled.txt" : "new-folder"}
                        depth={depth + 1}
                        indentBase={indentBase}
                        icon={creating.type === "file"
                            ? getFileIcon("untitled.txt", 14)
                            : <Package size={14} className="text-muted-foreground" />}
                        onCommit={(name) => createAssetEntry(creating.type, name)}
                        onCancel={() => setCreating(null)}
                    />
                </div>
            )}

            <ContextMenu
                open={ctxMenu.open}
                x={ctxMenu.x}
                y={ctxMenu.y}
                items={contextItems}
                onClose={() => setCtxMenu((state) => ({ ...state, open: false }))}
            />
        </>
    );
}

function HistorySnapshotSection({
    filePath,
    historyDir,
    depth,
    indentBase,
    onOpenFile,
}: {
    filePath: string;
    historyDir: string;
    depth: number;
    indentBase: number;
    onOpenFile: (path: string) => void;
}) {
    const t = useT("editor");
    const fileHistory = useHistoryStore((state) => state.files[filePath]);
    const loadHistoryIfNeeded = useHistoryStore((state) => state.loadIfNeeded);
    const removeSnapshot = useHistoryStore((state) => state.remove);
    const setSnapshotPreview = useEditorStore((state) => state.setSnapshotPreview);
    const setStatusMessage = useEditorStore((state) => state.setStatusMessage);
    const [deletingSnapshot, setDeletingSnapshot] = useState<string | null>(null);
    const [expandedSnapshots, setExpandedSnapshots] = useState<Record<string, boolean>>({});
    const [expandedSnapshotGroups, setExpandedSnapshotGroups] = useState<Record<string, boolean>>({});
    const [ctxMenu, setCtxMenu] = useState<{
        open: boolean;
        x: number;
        y: number;
        snapshot: SnapshotMeta | null;
    }>({ open: false, x: 0, y: 0, snapshot: null });

    useEffect(() => {
        loadHistoryIfNeeded(filePath).catch(() => {});
    }, [filePath, loadHistoryIfNeeded]);

    const snapshots = fileHistory?.snapshots ?? [];
    const rowStyle = { paddingLeft: `${indentBase + depth * 12}px` };
    const childStyle = { paddingLeft: `${indentBase + (depth + 1) * 12}px` };
    const grandChildStyle = { paddingLeft: `${indentBase + (depth + 2) * 12}px` };

    const closeCtx = () => setCtxMenu((state) => ({ ...state, open: false }));
    const snapshotItems = (snapshot: SnapshotMeta): ContextMenuItem[] => [
        {
            key: "preview",
            label: t("common.preview"),
            icon: <Eye size={14} />,
            onClick: () => setSnapshotPreview(filePath, snapshot.id),
        },
        {
            key: "delete",
            label: deletingSnapshot === snapshot.id
                ? t("explorer.history.deletingSnapshot")
                : t("explorer.history.deleteSnapshot"),
            icon: <Trash2 size={14} />,
            disabled: deletingSnapshot === snapshot.id,
            onClick: async () => {
                setDeletingSnapshot(snapshot.id);
                try {
                    await removeSnapshot(filePath, snapshot.id);
                    setStatusMessage(t("explorer.history.snapshotDeleted"));
                } catch {
                    setStatusMessage(t("explorer.history.snapshotDeleteFailed"));
                } finally {
                    setDeletingSnapshot(null);
                }
            },
        },
    ];
    const renderSnapshotFiles = (snapshot: SnapshotMeta) => {
        const docPath = `${historyDir}/doc/${snapshot.id}.md`;
        const externalRefs = snapshot.externalRefs ?? [];
        const docGroupKey = `${snapshot.id}:doc`;
        const externalGroupKey = `${snapshot.id}:external`;
        const docOpen = expandedSnapshotGroups[docGroupKey] ?? false;
        const externalOpen = expandedSnapshotGroups[externalGroupKey] ?? false;
        const toggleGroup = (key: string) =>
            setExpandedSnapshotGroups((state) => ({ ...state, [key]: !(state[key] ?? false) }));
        return (
            <>
                <button
                    type="button"
                    onClick={() => toggleGroup(docGroupKey)}
                    className="flex items-center w-full text-left text-[10px] py-0.5 pr-2 rounded hover:bg-muted/50 text-muted-foreground"
                    style={childStyle}
                    title={docPath}
                >
                    <span className="mr-0.5 inline-flex items-center">
                        {docOpen ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
                    </span>
                    <Package size={10} className="mr-1.5 shrink-0 opacity-70" />
                    <span className="truncate">doc</span>
                    <span className="ml-1.5 shrink-0 text-[9px] text-muted-foreground/50 font-mono">1</span>
                </button>
                {docOpen && (
                    <button
                        type="button"
                        onClick={() => onOpenFile(docPath)}
                        className="flex items-center w-full text-left text-[10px] py-0.5 pr-2 rounded hover:bg-muted/50 text-muted-foreground"
                        style={grandChildStyle}
                        title={docPath}
                    >
                        <File size={10} className="mr-1.5 shrink-0 opacity-70" />
                        <span className="truncate">{snapshot.id}.md</span>
                    </button>
                )}
                {externalRefs.length > 0 && (
                    <button
                        type="button"
                        onClick={() => toggleGroup(externalGroupKey)}
                        className="flex items-center w-full text-left text-[10px] py-0.5 pr-2 rounded hover:bg-muted/50 text-muted-foreground"
                        style={childStyle}
                        title={`${historyDir}/external/${snapshot.id}`}
                    >
                        <span className="mr-0.5 inline-flex items-center">
                            {externalOpen ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
                        </span>
                        <Package size={10} className="mr-1.5 shrink-0 opacity-70" />
                        <span className="truncate">external</span>
                        <span className="ml-1.5 shrink-0 text-[9px] text-muted-foreground/50 font-mono">
                            {externalRefs.length}
                        </span>
                    </button>
                )}
                {externalOpen &&
                    externalRefs.map((ref) => {
                        const externalPath = `${historyDir}/external/${snapshot.id}/${ref.storedPath}`;
                        return (
                        <button
                            key={`${snapshot.id}:${ref.storedPath}`}
                            type="button"
                            onClick={() => onOpenFile(externalPath)}
                            className="flex items-center w-full text-left text-[10px] py-0.5 pr-2 rounded hover:bg-muted/50 text-muted-foreground"
                            style={grandChildStyle}
                            title={externalPath}
                        >
                            <File size={10} className="mr-1.5 shrink-0 opacity-70" />
                            <span className="truncate">{ref.storedPath}</span>
                        </button>
                        );
                    })}
            </>
        );
    };

    return (
        <>
            {fileHistory?.loading && !fileHistory.loaded && (
                <div
                    className="text-[10px] text-muted-foreground italic py-1"
                    style={rowStyle}
                >
                    {t("common.loading")}
                </div>
            )}
            {fileHistory?.error && (
                <div className="text-[10px] text-red-500 italic py-1" style={rowStyle}>
                    {fileHistory.error}
                </div>
            )}
            {!fileHistory?.loading && !fileHistory?.error && snapshots.length === 0 && (
                <div className="text-[10px] text-muted-foreground/60 italic py-1" style={rowStyle}>
                    {t("explorer.history.noSnapshots")}
                </div>
            )}
            {snapshots.map((snapshot) => {
                const open = expandedSnapshots[snapshot.id] ?? false;
                const note = snapshotNote(snapshot);
                return (
                    <div key={snapshot.id}>
                        <button
                            type="button"
                            onClick={() =>
                                setExpandedSnapshots((state) => ({
                                    ...state,
                                    [snapshot.id]: !open,
                                }))
                            }
                            onDoubleClick={() => setSnapshotPreview(filePath, snapshot.id)}
                            onContextMenu={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                setCtxMenu({
                                    open: true,
                                    x: event.clientX,
                                    y: event.clientY,
                                    snapshot,
                                });
                            }}
                            className="flex items-center w-full text-left text-[10px] py-0.5 pr-2 rounded hover:bg-muted/50 text-muted-foreground"
                            style={rowStyle}
                            title={[snapshot.id, snapshot.createdAt, note].filter(Boolean).join("\n")}
                        >
                            <span className="mr-0.5 inline-flex items-center">
                                {open ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
                            </span>
                            <Clock size={10} className="mr-1.5 shrink-0 opacity-60" />
                            <span className="truncate font-mono">{formatRelative(snapshot.createdAt)}</span>
                            {note ? (
                                <span className="ml-1.5 truncate text-[9px] opacity-60">
                                    {note}
                                </span>
                            ) : null}
                            <span className="ml-1.5 shrink-0 text-[9px] opacity-50">
                                {triggerLabel(snapshot.trigger)}
                            </span>
                            {snapshot.externalRefs?.length ? (
                                <span className="ml-1.5 shrink-0 text-[9px] text-muted-foreground/50 font-mono">
                                    {snapshot.externalRefs.length + 1}
                                </span>
                            ) : null}
                        </button>
                        {open && renderSnapshotFiles(snapshot)}
                    </div>
                );
            })}
            {ctxMenu.open && (
                <ContextMenu
                    open={ctxMenu.open}
                    x={ctxMenu.x}
                    y={ctxMenu.y}
                    items={ctxMenu.snapshot ? snapshotItems(ctxMenu.snapshot) : []}
                    onClose={closeCtx}
                />
            )}
        </>
    );
}

function InstancesPackageNode({
    parsedDocument,
    filePath,
    depth,
    indentBase,
}: {
    parsedDocument: ParsedDocument;
    filePath: string;
    depth: number;
    indentBase: number;
}) {
    const [expanded, setExpanded] = useState(false);
    const instanceCount = countInstances(parsedDocument);

    if (instanceCount === 0) return null;

    return (
        <>
            <button
                type="button"
                onClick={() => setExpanded((value) => !value)}
                className="flex items-center w-full text-left text-xs py-1 pr-2 rounded transition-colors text-foreground/70 hover:bg-muted/60 opacity-70"
                style={{ paddingLeft: `${indentBase + depth * 12}px` }}
                title="instances"
            >
                <span className="w-4 h-4 flex items-center justify-center shrink-0">
                    {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                </span>
                <span className="mr-1.5 flex items-center">
                    <Package size={14} className="text-muted-foreground" />
                </span>
                <span className="truncate">instances</span>
                <span className="ml-1.5 text-[9px] text-muted-foreground/50 font-mono">
                    {instanceCount}
                </span>
            </button>
            {expanded && (
                <div style={{ paddingLeft: `${indentBase + (depth + 1) * 12}px` }}>
                    <PluginPanel
                        embedded
                        hideEmbeddedHeader
                        compact
                        parsedDocumentOverride={parsedDocument}
                        onSelectInstance={(id) => {
                            const store = useEditorStore.getState();
                            const tab = store.tabs.find((item) => item.filePath === filePath);
                            if (tab) {
                                store.setActiveTab(tab.id);
                            }
                            useEditorStore.getState().setSelectedInstanceId(id);
                        }}
                    />
                </div>
            )}
        </>
    );
}

function DocumentDoctorNode({
    filePath,
    depth,
    indentBase,
}: {
    filePath: string;
    depth: number;
    indentBase: number;
}) {
    const openDocumentDoctor = useEditorStore((state) => state.openDocumentDoctor);
    const isActive = useEditorStore((state) => {
        const activeTab = state.tabs.find((tab) => tab.id === state.activeTabId);
        return activeTab?.kind === "document-doctor" && activeTab.sourceFilePath === filePath;
    });

    return (
        <button
            type="button"
            onClick={() => openDocumentDoctor(filePath)}
            data-explorer-node-path={`${filePath}#doctor`}
            className={cn(
                "flex items-center w-full text-left text-xs py-1 pr-2 rounded transition-colors",
                isActive
                    ? "bg-primary/10 text-primary"
                    : "text-foreground/70 hover:bg-muted/60 opacity-70",
            )}
            style={{ paddingLeft: `${indentBase + depth * 12}px` }}
            title={`Doctor: ${filePath}`}
        >
            <span className="w-4 shrink-0" />
            <span className="mr-1.5 flex items-center">
                <Stethoscope size={14} className="text-muted-foreground" />
            </span>
            <span className="truncate">doctor</span>
        </button>
    );
}

export interface DocumentFileTreeNodeProps {
    filePath: string;
    name?: string;
    label?: string;
    depth: number;
    indentBase?: number;
    bundlePath?: string | null;
    initialBundleChildren?: FileTreeNode[];
    initialBundleLoaded?: boolean;
    initialExpanded?: boolean;
    persistBundleExpansion?: boolean;
    isActive?: boolean;
    isSelected?: boolean;
    isDirty?: boolean;
    isStarred?: boolean;
    isCycle?: boolean;
    rowClassName?: string;
    buttonRef?: Ref<HTMLButtonElement>;
    buttonProps?: ButtonHTMLAttributes<HTMLButtonElement>;
    suffix?: ReactNode;
    trailing?: ReactNode;
    contextItems?: ContextMenuItem[];
    extraChildren?: ReactNode;
    showDoctorNode?: boolean;
    onOpenFile?: (path: string) => void;
    onActivate?: () => void;
    onSelect?: (path: string, event: ReactMouseEvent) => boolean | void;
}

export function DocumentFileTreeNode({
    filePath,
    name,
    label,
    depth,
    indentBase = 8,
    bundlePath,
    initialBundleChildren,
    initialBundleLoaded = false,
    initialExpanded = false,
    persistBundleExpansion = false,
    isActive = false,
    isSelected = false,
    isDirty = false,
    isStarred = false,
    isCycle = false,
    rowClassName,
    buttonRef,
    buttonProps,
    suffix,
    trailing,
    contextItems,
    extraChildren,
    showDoctorNode = true,
    onOpenFile,
    onActivate,
    onSelect,
}: DocumentFileTreeNodeProps) {
    const openFileFromPath = useEditorStore((state) => state.openFileFromPath);
    const toggleTreeNode = useEditorStore((state) => state.toggleTreeNode);
    const parsedDocumentForFile = useEditorStore((state) => {
        const tab = state.tabs.find((item) => item.filePath === filePath);
        if (!tab) return null;
        return state.parsedDocumentMap.get(tab.id) ??
            (tab.id === state.activeTabId ? state.parsedDocument : null);
    });
    const hasInitialBundleChildren = (initialBundleChildren?.length ?? 0) > 0;
    const [resolvedBundlePath, setResolvedBundlePath] = useState<string | null>(
        bundlePath ?? null,
    );
    const [bundleKnown, setBundleKnown] = useState(
        !!bundlePath || hasInitialBundleChildren,
    );
    const [realBundleKnown, setRealBundleKnown] = useState(
        !!bundlePath || hasInitialBundleChildren,
    );
    const [expanded, setExpanded] = useState(initialExpanded);
    const [children, setChildren] = useState<FileTreeNode[]>(
        initialBundleChildren ?? [],
    );
    const [loaded, setLoaded] = useState(initialBundleLoaded || hasInitialBundleChildren);
    const [ctxMenu, setCtxMenu] = useState<{
        open: boolean;
        x: number;
        y: number;
    }>({ open: false, x: 0, y: 0 });

    const displayName = name ?? filePath.split("/").pop() ?? filePath;
    const displayLabel = label ?? displayName;
    const hasDoctorNode = showDoctorNode && /\.md$/i.test(filePath) && !isCycle;
    const hasExtraChildren = !!extraChildren;
    const hasInstancesPackage = countInstances(parsedDocumentForFile) > 0;
    const hasTraceNode = /\.md$/i.test(filePath) && !isCycle;
    const isExpandable =
        hasDoctorNode ||
        bundleKnown ||
        hasExtraChildren ||
        hasInstancesPackage ||
        hasTraceNode;

    useEffect(() => {
        if (bundlePath || initialBundleChildren?.length || !/\.md$/i.test(filePath)) {
            return;
        }
        let cancelled = false;
        const candidate = documentBundlePath(filePath);
        if (!candidate) return;
        let engine: WorkspaceEngine;
        try {
            engine = getPlatformService().engine;
        } catch {
            return;
        }
        safeReadDir(engine, candidate).then((entries) => {
            if (cancelled) return;
            if (entries.length > 0) {
                setResolvedBundlePath(candidate);
                setBundleKnown(true);
                setRealBundleKnown(true);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [bundlePath, filePath, initialBundleChildren?.length]);

    useEffect(() => {
        if (bundlePath) {
            setResolvedBundlePath(bundlePath);
            setBundleKnown(true);
            setRealBundleKnown(true);
        }
        if (initialBundleChildren) {
            setRealBundleKnown((current) => current || initialBundleChildren.length > 0);
            setChildren((current) => mergeVirtualChildrenWithExistingState(
                initialBundleChildren,
                current,
                {
                    preserveExistingWhenNextEmpty: true,
                    preserveExistingUnmatched: true,
                },
            ));
            setLoaded(initialBundleLoaded || initialBundleChildren.length > 0);
        }
        if (initialExpanded) {
            setExpanded(true);
        }
        if (persistBundleExpansion) {
            setExpanded(initialExpanded);
        }
    }, [
        bundlePath,
        initialBundleChildren,
        initialBundleLoaded,
        initialExpanded,
        persistBundleExpansion,
    ]);

    useEffect(() => {
        const traceDirPath = getDocumentTraceDirPath(filePath);
        if (!hasTraceNode) {
            setChildren((current) => current.filter((child) => child.path !== traceDirPath));
            return;
        }
        const targetBundlePath = traceDirPath.replace(/\/traces$/, "");
        setResolvedBundlePath(targetBundlePath);
        setBundleKnown(true);
        setChildren((current) => {
            if (current.some((child) => child.path === traceDirPath)) return current;
            return [...current, buildTraceDirectoryNode(filePath)]
                .sort((a, b) => a.name.localeCompare(b.name));
        });
    }, [filePath, hasTraceNode]);

    const ensureLoaded = useCallback(async () => {
        if (loaded || !resolvedBundlePath) return;
        const nextChildren = await loadVirtualChildren({
            name: displayName,
            path: filePath,
            isDirectory: false,
            bundlePath: resolvedBundlePath,
            children: [],
        });
        setChildren((current) =>
            mergeTraceDirectoryNode(filePath, nextChildren, current, hasTraceNode),
        );
        setLoaded(true);
    }, [displayName, filePath, hasTraceNode, loaded, resolvedBundlePath]);

    const reloadBundleChildren = useCallback(async () => {
        const targetBundlePath = resolvedBundlePath ?? documentBundlePath(filePath);
        if (!targetBundlePath) return;
        const nextChildren = await loadVirtualChildren({
            name: displayName,
            path: filePath,
            isDirectory: false,
            bundlePath: targetBundlePath,
            children: [],
        });
        setResolvedBundlePath(targetBundlePath);
        setBundleKnown(true);
        setChildren((current) => mergeVirtualChildrenWithExistingState(
            mergeTraceDirectoryNode(filePath, nextChildren, current, hasTraceNode),
            current,
        ));
        setLoaded(true);
    }, [displayName, filePath, hasTraceNode, resolvedBundlePath]);

    useEffect(() => {
        if (typeof window === "undefined") return;
        const handler = (event: Event) => {
            const detail = (event as CustomEvent<BundleRefreshDetail>).detail;
            if (!detail) return;
            const targetBundlePath = resolvedBundlePath ?? documentBundlePath(filePath);
            if (
                targetBundlePath &&
                detail.bundleDir === targetBundlePath &&
                detail.filePath?.startsWith(`${targetBundlePath}/configs/`) &&
                children.some((child) => child.name === "configs" || child.path === `${targetBundlePath}/configs`)
            ) {
                return;
            }
            if (
                detail.filePath === filePath ||
                (targetBundlePath && detail.bundleDir === targetBundlePath) ||
                (targetBundlePath && detail.publishDir?.startsWith(`${targetBundlePath}/`))
            ) {
                void reloadBundleChildren();
            }
        };
        window.addEventListener(BUNDLE_REFRESH_EVENT, handler);
        return () => window.removeEventListener(BUNDLE_REFRESH_EVENT, handler);
    }, [children, filePath, reloadBundleChildren, resolvedBundlePath]);

    const handleOpen = useCallback(
        (event: ReactMouseEvent) => {
            if (isCycle) return;
            if (onSelect?.(filePath, event) === false) return;
            if (onActivate) {
                onActivate();
                return;
            }
            if (onOpenFile) {
                onOpenFile(filePath);
                return;
            }
            void openFileFromPath(filePath);
        },
        [filePath, isCycle, onActivate, onOpenFile, onSelect, openFileFromPath],
    );

    const handleChevronClick = useCallback(
        (event: ReactMouseEvent) => {
            event.preventDefault();
            event.stopPropagation();
            if (persistBundleExpansion && realBundleKnown) {
                void toggleTreeNode(filePath);
                return;
            }
            setExpanded((next) => {
                const expandedNext = !next;
                if (expandedNext) {
                    void ensureLoaded();
                }
                return expandedNext;
            });
        },
        [ensureLoaded, filePath, persistBundleExpansion, realBundleKnown, toggleTreeNode],
    );

    return (
        <>
            <button
                type="button"
                ref={buttonRef}
                {...buttonProps}
                onClick={handleOpen}
                onContextMenu={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setCtxMenu({ open: true, x: event.clientX, y: event.clientY });
                }}
                data-explorer-node-path={filePath}
                aria-pressed={isSelected}
                className={cn(
                    "flex items-center w-full text-left text-xs py-1 pr-2 rounded transition-colors group",
                    isCycle
                        ? "text-muted-foreground/40 cursor-default italic"
                        : isSelected
                            ? "bg-primary/15 text-primary"
                            : isActive
                                ? "bg-primary/10 text-primary"
                                : "text-foreground/80 hover:bg-muted/60",
                    rowClassName,
                )}
                style={{ paddingLeft: `${indentBase + depth * 12}px` }}
                title={filePath}
            >
                {isExpandable ? (
                    <span
                        className="w-4 h-4 flex items-center justify-center shrink-0"
                        role="button"
                        tabIndex={-1}
                        onClick={handleChevronClick}
                    >
                        {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                    </span>
                ) : (
                    <span className="w-4 shrink-0" />
                )}
                <span className="mr-1.5 flex items-center">
                    {getFileIcon(displayName, 14)}
                </span>
                <span className="truncate">{displayLabel}</span>
                {suffix}
                {isStarred && (
                    <StarMark />
                )}
                {trailing}
                {isDirty && !trailing ? (
                    <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0 ml-auto" />
                ) : null}
            </button>

            {expanded && (
                <div>
                    {hasDoctorNode && (
                        <DocumentDoctorNode
                            filePath={filePath}
                            depth={depth + 1}
                            indentBase={indentBase}
                        />
                    )}
                    {parsedDocumentForFile && (
                        <InstancesPackageNode
                            parsedDocument={parsedDocumentForFile}
                            filePath={filePath}
                            depth={depth + 1}
                            indentBase={indentBase}
                        />
                    )}
                    {children.map((child) => (
                        <VirtualBundleNode
                            key={child.path}
                            node={child}
                            depth={depth + 1}
                            indentBase={indentBase}
                            onOpenFile={onOpenFile ?? ((path) => void openFileFromPath(path))}
                            parsedDocument={parsedDocumentForFile}
                            persistExpansion={persistBundleExpansion}
                        />
                    ))}
                    {extraChildren}
                </div>
            )}

            {contextItems && (
                <ContextMenu
                    open={ctxMenu.open}
                    x={ctxMenu.x}
                    y={ctxMenu.y}
                    items={contextItems}
                    onClose={() => setCtxMenu((state) => ({ ...state, open: false }))}
                />
            )}
        </>
    );
}

function StarMark() {
    const t = useT("editor");
    return (
        <Star
            size={10}
            className="ml-1 shrink-0 text-amber-500 fill-amber-500"
            aria-label={t("explorer.starred")}
        />
    );
}
